import {
  MRT_GlobalFilterTextField as MRTGlobalFilterTextField,
  MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
  MRT_TableInstance as MRTTableInstance,
  MRT_ToggleFiltersButton as MRTToggleFiltersButton
} from "material-react-table";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { AddBox, Delete } from "@mui/icons-material";

type Props<TData extends Record<string, unknown>> = {
  table: MRTTableInstance<TData>;
  /** When omitted or whitespace-only and no `titleActions`, the title row is hidden. */
  title?: string;
  createBtnLabel: string;
  /**
   * When set, overrides the default MRT behaviour (`table.setCreatingRow(true)`).
   * Use for lists that open a custom dialog instead of inline create rows.
   */
  onCreateClick?: () => void;
  /** `data-testid` for the add button (default: Add-new-item-btn). */
  createButtonTestId?: string;
  /** When true, hides the column filter toggle (e.g. no column filtering enabled). */
  hideFilterToggle?: boolean;
  isEditable?: boolean;
  /** When true, hides the "Add" button (e.g. visitor bookings list has no create row). */
  hideCreateButton?: boolean;
  disabledDeleteButton: boolean;
  /** Rendered next to the title (e.g. fleet calendar/date range/column toggle buttons) */
  titleActions?: React.ReactNode;
  childComponent?: JSX.Element;
  importExportNode?: React.ReactNode;
  onDelete: () => void;
};
export const ListTopToolbar = <TData extends Record<string, unknown>>({
  table,
  title,
  createBtnLabel,
  onCreateClick,
  createButtonTestId = "Add-new-item-btn",
  hideFilterToggle = false,
  isEditable = true,
  hideCreateButton = false,
  disabledDeleteButton,
  titleActions,
  childComponent,
  importExportNode,
  onDelete
}: Props<TData>) => {
  const { t } = useTranslation();

  return (
    <Box
      data-testid="list-top-toolbar-parent"
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        p: 2,
        gap: 2
      }}
    >
      {/* TOP ROW: Left section (title) + Right section (MRT actions and buttons) */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          justifyContent: { lg: "space-between" },
          alignItems: { xs: "flex-start", lg: "center" },
          gap: 2
        }}
      >
        {/* LEFT SECTION: Title + optional title actions (e.g. fleet toggles) */}
        <Box
          data-testid="list-top-toolbar-title-and-actions"
          sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
        >
          <Typography variant={"h5"} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
            {title}
          </Typography>
          {titleActions}
        </Box>

        {/* RIGHT SECTION: MRT Actions and Buttons */}
        <Box
          data-testid="list-top-toolbar-mrt-actions-and-buttons"
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: { xs: "flex-start", sm: "space-between" },
            gap: 1,
            borderTop: { xs: "1px solid #eee", sm: "none" },
            pt: { xs: 2, sm: 0 }
          }}
        >
          <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 260px" }, maxWidth: { sm: "400px" } }}>
            <MRTGlobalFilterTextField table={table} />
          </Box>

          <Box
            sx={{
              display: "flex",
              width: { xs: "100%", sm: "auto" },
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1
            }}
          >
            {!hideFilterToggle ? <MRTToggleFiltersButton table={table} /> : null}
            <MRTShowHideColumnsButton table={table} />

            {isEditable && (
              <Tooltip title={t("Delete selection")}>
                <span>
                  <IconButton
                    color="error"
                    data-testid="handle-remove-item-btn"
                    disabled={disabledDeleteButton}
                    onClick={onDelete}
                    size="large"
                  >
                    <Delete />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {importExportNode}
          </Box>

          {isEditable && (
            <>
              {!hideCreateButton ? (
                <Button
                  data-testid={createButtonTestId}
                  onClick={() => {
                    if (onCreateClick) {
                      onCreateClick();
                      return;
                    }
                    table.setCreatingRow(true);
                  }}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  <AddBox sx={{ mr: 1 }} />
                  {createBtnLabel}
                </Button>
              ) : null}
            </>
          )}
        </Box>
      </Box>

      {/* BELOW: child component (e.g. fleet date range + column toggles) */}
      {childComponent && <Box sx={{ width: "100%" }}>{childComponent}</Box>}
    </Box>
  );
};
